import _ from "lodash";
import { isDisplayable } from "util/misc";
import {
  getSeasonTeamIdBySide,
  getOppositeSide,
  getTeamSideFromId,
  isPlayInterruption
} from "games/utils";

const playsWithNoDown = [
  "touchback",
  "kickoffReturn",
  "fairCatch",
  "puntReturn",
  "fumbleRecoveryInEndZone",
  "interceptionReturn",
  "freeKickReturn",
  "blockedPuntRecoveryInEndZone"
];

const playsWithNoYardLine = [
  "interceptionReturn",
  "freeKickReturn",
  "blockedPuntRecoveryInEndZone",
  "fairCatch"
];

const playHasNoYardLine = (playType) => {
  return isPlayInterruption(playType) || playsWithNoYardLine.includes(playType);
};

const getQuarter = (previousPlay) => {
  const previousQuarter = isDisplayable(previousPlay)
    ? _.get(previousPlay, "quarter", 0)
    : 1;
  const previousPlayType = _.get(previousPlay, "playType");
  return previousPlayType === "endOfQuarter"
    ? previousQuarter + 1
    : previousQuarter;
};

const getIsGoalDistanceToGo = (
  previousPlay,
  isChangeInPossession,
  downIsReset
) => {
  const { ballSpot, ballSpotTeamId, teamInPossessionId } = previousPlay;
  const isOnOpponentsSideOfField = isChangeInPossession
    ? ballSpotTeamId === teamInPossessionId
    : ballSpotTeamId !== teamInPossessionId;
  const isNearEndZone = isOnOpponentsSideOfField && ballSpot <= 10;
  const { isGoalDistanceToGo = false } = previousPlay;
  const isReset = isChangeInPossession || downIsReset;
  // first: If within 10yd line && reset DTG active (condition covers first downs & turnovers)
  // second: If previous play was DTG and no down reset / turnover continue with DTG (2nd - 4th down)
  return (isNearEndZone && isReset) || (isGoalDistanceToGo && !isReset);
};

const getDistanceToGo = (previousPlay, playType, downIsReset) => {
  const { distanceToGo, lengthOfPlay } = previousPlay;

  if (isPlayInterruption(playType)) {
    return null;
  }

  const distanceToGoTop = 10;

  if (playsWithNoDown.includes(playType)) {
    return 0;
  } else if (downIsReset) {
    return distanceToGoTop;
  } else {
    const remainder = distanceToGo - lengthOfPlay;
    return remainder > 0 ? remainder : distanceToGoTop;
  }
};

const getTeamInPossessionId = (previousPlay, game, isChangeInPossession) => {
  const { teamInPossessionId, quarter, playType } = previousPlay;
  // cant predict team in possession here, so leave blank
  if (playType === "endOfQuarter" && quarter === 2) {
    return null;
  } else if (isChangeInPossession) {
    const teamInPossessionSide = getTeamSideFromId(game, teamInPossessionId);
    return getSeasonTeamIdBySide(game, getOppositeSide(teamInPossessionSide));
  } else {
    return teamInPossessionId || "";
  }
};

const getDownIsReset = (previousPlay, isChangeInPossession) => {
  const {
    distanceToGo = 0,
    lengthOfPlay = 0,
    down,
    convertsFirstDown = false,
    playType,
    quarter
  } = previousPlay;

  return (
    convertsFirstDown ||
    isChangeInPossession ||
    (playType === "endOfQuarter" && quarter === 2) ||
    playType === "muffedPunt" ||
    lengthOfPlay >= distanceToGo ||
    down === 0
  );
};

const getIsChangeInPossession = (previousPlay, playType) => {
  const {
    fumble,
    playType: previousPlayType,
    muffedPuntRecoveredByTeamId,
    teamInPossessionId,
    down,
    includesTouchdown = false,
    lengthOfPlay = 0,
    distanceToGo = 0
  } = previousPlay;
  const fumbleLost = fumble === "lost";
  const changePlayTypes = [
    "punt",
    "PATPass",
    "PATKick",
    "PATRun",
    "fieldGoal",
    "safety"
  ];
  const muffedPuntRecoverdByDefense =
    isDisplayable(muffedPuntRecoveredByTeamId) &&
    muffedPuntRecoveredByTeamId !== teamInPossessionId;
  const isNonConverting = down === 4 && lengthOfPlay < distanceToGo;
  const penalties = ["penaltyDefense", "penaltyOffense"];

  return (
    muffedPuntRecoverdByDefense ||
    changePlayTypes.includes(previousPlayType) ||
    (isNonConverting &&
      !includesTouchdown &&
      !penalties.includes(previousPlay.playType)) ||
    playType === "interceptionReturn" ||
    // fumble refers to defensive player who catches the ball in
    // an interception
    (previousPlayType === "interception" && !fumbleLost) ||
    (previousPlayType !== "interception" && fumbleLost)
  );
};

const getDown = (previousPlay, playType, downIsReset) => {
  const { down } = previousPlay;
  const penalties = ["penaltyDefense", "penaltyOffense"];
  const downResetAtEndOfQuarter = downIsReset && playType === "endOfQuarter";
  const previousPlayIsPenalty =
    previousPlay &&
    previousPlay.playType &&
    penalties.includes(previousPlay.playType);
  // @todo keep down for timeout?
  if (isPlayInterruption(playType)) {
    return null;
  } else if (playsWithNoDown.includes(playType) || downResetAtEndOfQuarter) {
    return 0;
  } else if (previousPlayIsPenalty || playType === "endOfQuarter") {
    return down; // Edge cases where down should remain unchanged
  } else if (downIsReset) {
    return 1; // Either first down achieved or team swap
  } else {
    return down + 1; // default behavior, increase down by 1
  }
};

const getIncludesTouchdown = (playType) => {
  if (
    playType === "fumbleRecoveryInEndZone" ||
    playType === "blockedPuntRecoveryInEndZone"
  ) {
    return true;
  } else {
    return null;
  }
};

const getYardLine = (previousPlay, playType) => {
  if (playHasNoYardLine(playType)) {
    return null;
  } else {
    return _.get(previousPlay, "ballSpot", null);
  }
};

const getYardLineTeamId = (previousPlay, playType) => {
  if (playHasNoYardLine(playType)) {
    return null;
  } else {
    return _.get(previousPlay, "ballSpotTeamId", null);
  }
};

const getBallSpot = (playType) => {
  if (playType === "touchback") {
    return 20;
  } else if (playType === "blockedPuntRecoveryInEndZone") {
    return 0;
  } else {
    return null;
  }
};

const getBallSpotTeamId = (playType, teamInPossessionId, game) => {
  if (playType === "blockedPuntRecoveryInEndZone") {
    return getSeasonTeamIdBySide(
      game,
      getOppositeSide(getTeamSideFromId(game, teamInPossessionId))
    );
  } else {
    return null;
  }
};

export {
  getQuarter,
  getDistanceToGo,
  getTeamInPossessionId,
  getDownIsReset,
  getIsChangeInPossession,
  getDown,
  getIncludesTouchdown,
  getYardLine,
  getYardLineTeamId,
  getIsGoalDistanceToGo,
  getBallSpot,
  getBallSpotTeamId
};
